import { RouteComponentProps } from "react-router";

export interface DelayResult {
    promise: Promise<void>;
    cancel();
    finish();
}

function delay(delayMs: number): DelayResult {
    let timeout: number | null = null;
    let _resolve: (() => void) | null = null;
    let _reject: (() => void) | null = null;
    const promise = new Promise<void>((resolve, reject) => {
        _resolve = resolve;
        _reject = reject;
        timeout = window.setTimeout(resolve, delayMs);
    });
    return {
        promise,
        cancel() {
            if (timeout) {
                clearTimeout(timeout);
                timeout = null;
                _reject!();
            }
        },
        finish() {
            if (timeout) {
                clearTimeout(timeout);
                timeout = null;
                _resolve!();
            }
        },
    };
}

export class AsyncDataUpdate {
    promise: Promise<void>;
    updating = false;
    private finishNow = false;
    private sleep?: DelayResult;
    constructor(prevUpdate: Promise<any> | undefined, public update: () => Promise<any>, delayMs: number) {
        this.promise = new Promise<void>(async (resolve, reject) => {
            try {
                if (prevUpdate) {
                    await prevUpdate;
                }
                if (!this.finishNow) {
                    const sleep = delay(delayMs);
                    this.sleep = sleep;
                    await sleep.promise;
                }
                this.updating = true;
                await this.update();
                resolve();
            } catch {
                reject();
            }
        });
    }

    async updateNow() {
        this.finishNow = true;
        if (this.sleep) {
            this.sleep.finish();
            this.sleep = undefined;
        }
        await this.promise;
    }
}

export class DeferredUpdater {
    lastUpdate?: AsyncDataUpdate;

    scheduleUpdate(update: () => Promise<any>) {
        if (this.lastUpdate && !this.lastUpdate.updating) {
            this.lastUpdate.update = update;
        } else {
            this.lastUpdate = new AsyncDataUpdate(this.lastUpdate?.promise, update, 1000);
        }
    }

    async updateNow() {
        if (this.lastUpdate) {
            await this.lastUpdate.updateNow();
        }
    }
}

export const deferredUpdater = new DeferredUpdater();

export function getEntityId(id: string | null) {
    return id == null || id.toLowerCase() === "new" ? null : parseInt(id);
}

export function getId(props: RouteComponentProps) {
    return getEntityId(props.match.params["id"]);
}

export function getBoardId(props: RouteComponentProps) {
    return getEntityId(props.match.params["boardId"]);
}

export function getBoardTitle(board: { boardId: number; title: string; }) {
    return board.title || `Board ${board.boardId}`
}
